import { PenLine } from 'lucide-react';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import type { LucideIcon } from 'lucide-react';

interface SignatureFieldProps {
  value: string;
  onChange: (value: string) => void;
  label: string;
  description: string;
  /** Optional consent text displayed in a muted box above the input */
  consentText?: string;
  /** Input id for label association */
  id?: string;
  /** Icon override (defaults to PenLine) */
  icon?: LucideIcon;
  /** Whether to wrap in a Card. Defaults to true. */
  asCard?: boolean;
  /** Required indicator on label */
  required?: boolean;
}

export function SignatureField({
  value,
  onChange,
  label,
  description,
  consentText,
  id = 'signature-field',
  icon: Icon = PenLine,
  asCard = true,
  required = false,
}: SignatureFieldProps) {
  const content = (
    <div className="space-y-4">
      {consentText && (
        <div className="p-4 bg-slate-50 rounded-lg text-sm text-muted-foreground">
          <p>{consentText}</p>
        </div>
      )}
      <div className="space-y-2">
        <Label htmlFor={id}>
          {label} {required && <span className="text-red-500">*</span>}
        </Label>
        <p className="text-sm text-muted-foreground">{description}</p>
        <Input
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Type your full legal name"
          className="font-serif text-lg italic max-w-md"
        />
      </div>
    </div>
  );

  if (!asCard) return content;

  return (
    <Card className="border-primary">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Icon className="h-5 w-5" />
          Electronic Signature
        </CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        {consentText && (
          <div className="p-4 bg-slate-50 rounded-lg text-sm text-muted-foreground mb-4">
            <p>{consentText}</p>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor={id}>
            Type your full name as your electronic signature {required && <span className="text-red-500">*</span>}
          </Label>
          <Input
            id={id}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Type your full legal name"
            className="max-w-md"
          />
        </div>
      </CardContent>
    </Card>
  );
}
